/**
 * POPSTAR profile — ONE big landscape "VIP member card" (owner spec, 2026-07-07):
 * a tastefully organized membership card that showcases everything others see on
 * the public `cards/{uid}` doc plus the account economy at a glance —
 *   • identity: avatar, name, rank chip + tier, the XP bar toward the next level;
 *   • the EQUIPPED slot-1 sticker, framed like a collectible;
 *   • the FEATURED starred replay (score / duration / how it ended) with Watch;
 *   • star points (the token wallet), member-since, and the collection tallies.
 *
 * The card is a SHOWCASE, not a manager: replay browsing/starring lives under
 * Stats → My Replays, so the "Change" / "Choose a replay" actions here hand off
 * via {@link ProfileScreenProps.onChangeReplay} and the shell navigates there.
 * Likewise an empty sticker frame offers {@link ProfileScreenProps.onGoStickers}.
 *
 * NAMED-ONLY: the parent ({@link import('./NetworkShell').NetworkShell}) only
 * mounts this for `named` users, but we still defend against
 * {@link RequiresAccountError} (and any load failure) with an inline retry.
 *
 * Presentational + human-timescale: one-shot load via the {@link GameServices}
 * facade (`getProfile` / `getStickerCatalog` / `getMyReplays`), level math from
 * the pure {@link levelForXp} core helper, no per-frame state.
 */
import { useCallback, useEffect, useState } from 'react';
import { levelForXp } from '../core/xp';
import { rankForLevel } from '../core/ranks';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { RunSummary, StickerCatalogItem, UserProfile } from '../services/dataModel';
import { RankBadge } from './RankBadge';
import { useT } from './i18n';

interface ProfileScreenProps {
  services: GameServices;
  /** Back to the network shell hub. */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own back-header. */
  embedded?: boolean;
  /**
   * Watch the user's featured starred replay. Given the `runs/{runId}` id, the
   * shell loads it (via {@link GameServices.getReplay}) and plays it back. Optional:
   * when omitted, the card shows the starred run but hides the Watch button.
   */
  onWatch?: (runId: string) => void;
  /**
   * Set/change the featured replay. The card doesn't manage replays itself — the
   * shell navigates to Stats → My Replays where the user stars their pick.
   */
  onChangeReplay?: () => void;
  /** Equip a sticker (empty frame CTA) — the shell navigates to Stickers. */
  onGoStickers?: () => void;
}

/**
 * Resolve the FEATURED starred run the same way the public card does: the
 * most-recently-starred id (last of {@link UserProfile.starredReplayIds}) matched
 * against the loaded replay summaries. Returns null when nothing is starred or the
 * id isn't among the loaded runs.
 */
function findFeaturedStarred(
  profile: UserProfile,
  replays: RunSummary[],
): RunSummary | null {
  const ids = profile.starredReplayIds;
  if (ids.length === 0) return null;
  const featuredId = ids[ids.length - 1];
  return replays.find((r) => r.id === featuredId) ?? null;
}

/** Local load lifecycle for the one-shot profile fetch. */
type LoadState = 'loading' | 'ready' | 'error';

/** Resolve a sticker id to its catalog row (for the equipped showcase art / title). */
function findSticker(
  catalog: StickerCatalogItem[],
  id: string | null,
): StickerCatalogItem | null {
  if (!id) return null;
  return catalog.find((s) => s.id === id) ?? null;
}

/** Human-readable label for which attack family ended the run (or a fallback). */
function deathLabel(family: RunSummary['deathFamily']): string {
  if (!family) return 'Survived';
  // Capitalise the family token ('walls' → 'Walls') for the caption.
  return family.charAt(0).toUpperCase() + family.slice(1);
}

/** "Jun 2026" from epoch ms (locale-aware); empty for a missing/garbage stamp. */
function formatMemberSince(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '';
  return new Date(epochMs).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
}

/** mm:ss.S from milliseconds (e.g. 92_400 → "1:32.4"). */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00.0';
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths = Math.floor((totalSeconds * 10) % 10);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
}

export function ProfileScreen({
  services,
  onBack,
  onWatch,
  onChangeReplay,
  onGoStickers,
  embedded = false,
}: ProfileScreenProps) {
  const t = useT();
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [catalog, setCatalog] = useState<StickerCatalogItem[]>([]);
  const [replays, setReplays] = useState<RunSummary[]>([]);

  const load = useCallback(async () => {
    setState('loading');
    setError(null);
    try {
      const [p, cat, runs] = await Promise.all([
        services.getProfile(),
        services.getStickerCatalog(),
        services.getMyReplays(),
      ]);
      setProfile(p);
      setCatalog(cat);
      setReplays(runs);
      setState('ready');
    } catch (err) {
      setError(
        err instanceof RequiresAccountError
          ? 'Log in to view your profile.'
          : err instanceof Error
            ? err.message
            : 'Could not load your profile.',
      );
      setState('error');
    }
  }, [services]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={'screen network-screen profile-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{PROFILE_CSS}</style>
      {!embedded && (
        <header className="network-head">
          <button className="btn btn-ghost network-back" onClick={onBack} type="button">
            ‹ Back
          </button>
          <h1 className="network-title">{t('profile.title')}</h1>
        </header>
      )}

      {state === 'loading' && <p className="muted-line">{t('profile.loading')}</p>}

      {state === 'error' && (
        <div className="panel">
          <p className="auth-error">{error}</p>
          <button className="btn btn-ghost" type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {state === 'ready' && profile && (
        <VipCard
          profile={profile}
          catalog={catalog}
          replays={replays}
          onWatch={onWatch}
          onChangeReplay={onChangeReplay}
          onGoStickers={onGoStickers}
        />
      )}
    </div>
  );
}

/** The landscape VIP member card (kept separate so it can assume non-null data). */
function VipCard({
  profile,
  catalog,
  replays,
  onWatch,
  onChangeReplay,
  onGoStickers,
}: {
  profile: UserProfile;
  catalog: StickerCatalogItem[];
  replays: RunSummary[];
  onWatch?: (runId: string) => void;
  onChangeReplay?: () => void;
  onGoStickers?: () => void;
}) {
  const t = useT();
  const lvl = levelForXp(profile.xp);
  const rank = rankForLevel(lvl.level);
  // Guard against a zero/garbage span so the bar stays a sane fraction.
  const span = lvl.intoLevel + lvl.toNext;
  const pct = span > 0 ? Math.round(Math.max(0, Math.min(1, lvl.intoLevel / span)) * 100) : 0;

  const equipped = findSticker(catalog, profile.equipped.slot1);
  const featured = findFeaturedStarred(profile, replays);
  const since = formatMemberSince(profile.createdAt);

  return (
    <section className="vip-frame">
      <div className="vip-card">
        <span className="vip-sheen" aria-hidden />

        {/* Banner: the membership kicker + the star-point wallet. */}
        <header className="vip-banner">
          <span className="vip-kicker">
            POPSTAR <span className="vip-kicker-star" aria-hidden>✦</span> {t('profile.vipKicker')}
          </span>
          <span className="vip-points" title={t('profile.starPoints')}>
            <span className="vip-points-value">
              <span aria-hidden>◈</span> {profile.tokens.toLocaleString()}
            </span>
            <span className="vip-points-label">{t('profile.starPoints')}</span>
          </span>
        </header>

        {/* Identity row: avatar + name/rank/XP on the left, equipped sticker right. */}
        <div className="vip-main">
          <div className="vip-avatar" aria-hidden>
            {profile.name.charAt(0).toUpperCase() || '★'}
          </div>

          <div className="vip-id">
            <span className="vip-name">{profile.name}</span>
            <span className="vip-rank-row">
              <RankBadge level={lvl.level} variant="chip" />
              <span className="vip-tier">
                {rank.tierRoman} · {rank.tierName}
              </span>
            </span>
            <span
              className="vip-xp"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label={`Experience toward level ${lvl.level + 1}`}
            >
              <i style={{ width: `${pct}%` }} />
            </span>
            <span className="vip-xp-caption">
              {lvl.intoLevel.toLocaleString()} / {span.toLocaleString()} XP ·{' '}
              {lvl.toNext.toLocaleString()} {t('profile.xpToNext')}
            </span>
          </div>

          <div className="vip-equip">
            <button
              className="vip-equip-frame"
              type="button"
              data-filled={equipped ? 'true' : 'false'}
              title={equipped ? equipped.title : t('profile.noEquipped')}
              onClick={onGoStickers}
              disabled={!onGoStickers}
            >
              {equipped ? (
                <img className="vip-equip-art" src={equipped.imageUrl} alt={equipped.title} />
              ) : (
                <span className="vip-equip-empty" aria-hidden>
                  ◇
                </span>
              )}
            </button>
            <span className="vip-equip-label">
              {equipped ? equipped.title : t('profile.equipCta')}
            </span>
          </div>
        </div>

        {/* Featured (starred) replay strip — the shareable moment. */}
        <div className="vip-replay">
          <span className="vip-replay-label">
            <span className="vip-replay-star" aria-hidden>
              ★
            </span>
            {t('profile.featuredReplay')}
          </span>

          {featured ? (
            <div className="vip-replay-row">
              <span className="vip-replay-score">{featured.score.toLocaleString()}</span>
              <span className="vip-replay-tags">
                <span className="vip-replay-tag">{formatDuration(featured.durationMs)}</span>
                <span className="vip-replay-tag vip-replay-tag-death">
                  {featured.deathFamily
                    ? t('family.' + featured.deathFamily, deathLabel(featured.deathFamily))
                    : deathLabel(featured.deathFamily)}
                </span>
              </span>
              <span className="vip-replay-actions">
                {onWatch && (
                  <button
                    className="btn btn-primary vip-replay-watch"
                    type="button"
                    onClick={() => onWatch(featured.id)}
                  >
                    ▶ {t('common.watch', 'Watch')}
                  </button>
                )}
                {onChangeReplay && (
                  <button className="btn btn-ghost vip-replay-change" type="button" onClick={onChangeReplay}>
                    {t('profile.changeReplay')}
                  </button>
                )}
              </span>
            </div>
          ) : (
            <div className="vip-replay-row vip-replay-empty">
              <span className="vip-replay-hint muted-line">{t('profile.starToFeature')}</span>
              {onChangeReplay && (
                <button className="btn btn-primary vip-replay-choose" type="button" onClick={onChangeReplay}>
                  {t('profile.chooseReplay')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer: membership vintage + the collection tallies. */}
        <footer className="vip-footer">
          {since && (
            <span className="vip-since">
              {t('profile.memberSince')} {since}
            </span>
          )}
          <span className="vip-counts">
            <span className="vip-count">
              {t('profile.skill')} <b>{profile.skillStickerCount.toLocaleString()}</b>
            </span>
            <span className="vip-count-dot" aria-hidden>
              ·
            </span>
            <span className="vip-count">
              {t('profile.seasonalCount')} <b>{profile.seasonStickerCount.toLocaleString()}</b>
            </span>
            <span className="vip-count-dot" aria-hidden>
              ·
            </span>
            <span className="vip-count">
              {t('profile.secret')} <b>{profile.secretStickerCount.toLocaleString()}</b>
            </span>
          </span>
        </footer>
      </div>
    </section>
  );
}

/**
 * Screen-scoped styles for the VIP card (nested under `.profile-screen`). Shared
 * primitives (`.panel`, `.btn`, `.muted-line`, the rank chip, CSS vars) come from
 * src/index.css; only the card's landscape layout + holo dressing lives here.
 */
const PROFILE_CSS = `
/* ---- The landscape card: a gradient "metal edge" frame around dark glass. ---- */
.profile-screen .vip-frame {
  width: min(720px, 96vw);
  padding: 1.5px;
  border-radius: 26px;
  background: linear-gradient(135deg, var(--accent), var(--accent-2) 45%, var(--gold));
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45), 0 0 44px var(--accent-glow);
}
.profile-screen .vip-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-radius: 24.5px;
  padding: 0 0 16px;
  overflow: hidden;
  background:
    radial-gradient(120% 90% at 88% -10%, rgba(255, 209, 102, 0.13), transparent 55%),
    radial-gradient(120% 100% at -10% 115%, rgba(255, 79, 157, 0.11), transparent 55%),
    var(--panel-strong);
}
/* A soft diagonal holo sheen drifting across the card face. */
.profile-screen .vip-sheen {
  position: absolute;
  inset: -60% -30%;
  pointer-events: none;
  background: linear-gradient(
    115deg,
    transparent 42%,
    rgba(255, 255, 255, 0.055) 50%,
    transparent 58%
  );
  animation: vip-sheen-drift 7s var(--ease) infinite;
}
@keyframes vip-sheen-drift {
  0%, 55% { transform: translateX(-38%); }
  85%, 100% { transform: translateX(38%); }
}
@media (prefers-reduced-motion: reduce) {
  .profile-screen .vip-sheen { animation: none; }
}

/* ---- Banner: kicker + star points. ---- */
.profile-screen .vip-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 22px 0;
}
.profile-screen .vip-kicker {
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  background: linear-gradient(96deg, var(--accent), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  padding-top: 4px;
}
.profile-screen .vip-kicker-star { letter-spacing: 0; }
.profile-screen .vip-points {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}
.profile-screen .vip-points-value {
  font-size: 22px;
  font-weight: 900;
  color: var(--gold);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  text-shadow: 0 0 18px rgba(255, 209, 102, 0.35);
}
.profile-screen .vip-points-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

/* ---- Main row: avatar + identity + equipped sticker. ---- */
.profile-screen .vip-main {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 18px;
  padding: 0 22px;
}
.profile-screen .vip-avatar {
  width: 68px;
  height: 68px;
  display: grid;
  place-items: center;
  border-radius: 20px;
  font-size: 30px;
  font-weight: 900;
  color: var(--accent-ink);
  background: linear-gradient(135deg, var(--accent), var(--gold));
  border: 2px solid var(--glass-border-strong);
  box-shadow: 0 6px 20px var(--accent-glow);
}
.profile-screen .vip-id {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.profile-screen .vip-name {
  font-size: 24px;
  font-weight: 900;
  color: var(--text);
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.profile-screen .vip-rank-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.profile-screen .vip-tier {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-dim);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.profile-screen .vip-xp {
  width: 100%;
  max-width: 320px;
  height: 7px;
  border-radius: var(--radius-pill);
  background: var(--panel-2);
  overflow: hidden;
}
.profile-screen .vip-xp i {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  background: linear-gradient(90deg, var(--accent), var(--gold));
  transition: width var(--t-slow) var(--ease);
}
.profile-screen .vip-xp-caption {
  font-size: 12px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}

/* Equipped sticker: a collectible frame; empty state invites equipping. */
.profile-screen .vip-equip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  max-width: 128px;
}
.profile-screen .vip-equip-frame {
  width: 92px;
  height: 92px;
  display: grid;
  place-items: center;
  padding: 0;
  border-radius: 18px;
  background: var(--panel-2);
  border: 1.5px dashed var(--glass-border-strong);
  overflow: hidden;
  cursor: pointer;
  transition: border-color var(--t-fast) var(--ease), box-shadow var(--t-fast) var(--ease),
    transform var(--t-fast) var(--ease);
}
.profile-screen .vip-equip-frame:disabled { cursor: default; }
.profile-screen .vip-equip-frame:hover:not(:disabled) { transform: translateY(-1px); }
.profile-screen .vip-equip-frame[data-filled='true'] {
  border-style: solid;
  border-color: var(--gold);
  box-shadow: 0 0 24px rgba(255, 209, 102, 0.32);
}
.profile-screen .vip-equip-art {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.profile-screen .vip-equip-empty {
  font-size: 36px;
  color: var(--muted);
  opacity: 0.55;
}
.profile-screen .vip-equip-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-dim);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- Featured replay strip. ---- */
.profile-screen .vip-replay {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 22px;
  padding: 12px 16px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
}
.profile-screen .vip-replay-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.profile-screen .vip-replay-star {
  color: var(--gold);
  font-size: 13px;
  line-height: 1;
}
.profile-screen .vip-replay-row {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.profile-screen .vip-replay-score {
  font-size: 30px;
  font-weight: 900;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(96deg, var(--accent-soft), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 2px 12px var(--accent-glow));
}
.profile-screen .vip-replay-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.profile-screen .vip-replay-tag {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-dim);
  background: var(--panel-strong);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 3px 10px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.profile-screen .vip-replay-tag-death {
  color: var(--accent-2);
  border-color: rgba(255, 79, 157, 0.3);
}
.profile-screen .vip-replay-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.profile-screen .vip-replay-actions .btn {
  padding: 9px 16px;
  font-size: 14px;
  white-space: nowrap;
}
.profile-screen .vip-replay-empty {
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.profile-screen .vip-replay-hint { font-size: 13px; }

/* ---- Footer: member-since + collection tallies. ---- */
.profile-screen .vip-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 22px;
}
.profile-screen .vip-since {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.profile-screen .vip-counts {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
.profile-screen .vip-count b {
  color: var(--text);
  font-variant-numeric: tabular-nums;
}
.profile-screen .vip-count-dot { opacity: 0.5; }

/* ---- Narrow phones: keep the landscape feel, tighten the grid. ---- */
@media (max-width: 560px) {
  .profile-screen .vip-main {
    grid-template-columns: auto 1fr;
    row-gap: 12px;
  }
  .profile-screen .vip-equip {
    grid-column: 1 / -1;
    flex-direction: row;
    max-width: none;
    justify-content: flex-start;
    gap: 12px;
  }
  .profile-screen .vip-equip-frame {
    width: 64px;
    height: 64px;
    border-radius: 14px;
  }
  .profile-screen .vip-equip-label { text-align: left; }
  .profile-screen .vip-replay-row { flex-wrap: wrap; }
  .profile-screen .vip-replay-actions { width: 100%; }
  .profile-screen .vip-replay-actions .btn { flex: 1; }
}
`;
